import type {ReactNode} from 'react';
import {MessageCircle, Send as SendIcon} from 'lucide-react';
import {CHANNEL_NAMES, type ChannelName} from '@api/notifications';

/**
 * Описание канала-бота для UI подключения уведомлений.
 *
 * Каналы (Telegram, MAX) отличаются только оформлением и ссылкой на бота —
 * сценарий подключения (init → код → проверка) и все компоненты общие.
 * Добавить новый канал = добавить его в `ChannelName` и запись сюда.
 */
export interface NotificationChannelMeta {
  name: ChannelName;
  /** Короткое имя для строк списка и кнопок-тоглов. */
  label: string;
  /** Заголовок карточки в шаге выбора канала. */
  cardTitle: string;
  cardDescription: string;
  /** Заголовок окна подключения. */
  connectTitle: string;
  /** Подпись кнопки перехода в мессенджер. */
  openLabel: string;
  /** Фирменный цвет (CSS-переменная из index.css). */
  brandColor: string;
  /** Постоянная ссылка на бота — фолбэк, если бэк не вернул `botLink`. */
  botUrl: string;
  icon: (size: number) => ReactNode;
}

const TG_BOT_USERNAME = 'colver_support_bot';
const MAX_BOT_URL = 'https://max.ru/id723000969508_bot';

const CHANNEL_META: Record<ChannelName, NotificationChannelMeta> = {
  telegram: {
    name: 'telegram',
    label: 'Telegram',
    cardTitle: 'Telegram bot',
    cardDescription: 'Мгновенные пуши прямо в мессенджер',
    connectTitle: 'Подключение Telegram bot',
    openLabel: 'Перейти в Telegram',
    brandColor: 'var(--brand-telegram)',
    botUrl: `https://t.me/${TG_BOT_USERNAME}`,
    icon: (size) => <SendIcon size={size} />,
  },
  max: {
    name: 'max',
    label: 'MAX',
    cardTitle: 'MAX bot',
    cardDescription: 'Мгновенные пуши прямо в мессенджер',
    connectTitle: 'Подключение MAX bot',
    openLabel: 'Перейти в MAX',
    brandColor: 'var(--brand-max)',
    botUrl: MAX_BOT_URL,
    icon: (size) => <MessageCircle size={size} />,
  },
};

/** Метаданные канала по имени. */
export const channelMeta = (name: ChannelName): NotificationChannelMeta =>
  CHANNEL_META[name];

/** Все каналы в порядке отображения. */
export const NOTIFICATION_CHANNELS: NotificationChannelMeta[] =
  CHANNEL_NAMES.map((name) => CHANNEL_META[name]);
